import Service, { service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import { resolveDemoScenario } from 'tio-employee/demo/registry';
import type { FetchMatcher } from 'tio-data/demo/types';
import type FeaturesService from 'tio-common/services/features';
import { CAPABILITY_SOURCE_RANK, type CapabilitySource } from 'tio-common/services/features';
import type SessionContextService from 'tio-employee/services/session-context';
import {
  DEMO_CLIENT_LIST,
  DEMO_PRODUCT_LIST,
  DEMO_SESSION_ROLE_LIST,
  DEMO_TA_PAYMENT_MODEL_LIST,
  DEMO_USER_SCENARIO_LIST,
  applyDemoSession,
  getCurrentDemoClientId,
  getCurrentDemoProductId,
  getCurrentDemoRoleId,
  getCurrentDemoTaPaymentModelId,
  getCurrentDemoUserScenarioId,
  isFeatureEnabled,
  getDemoData,
  getDemoProduct,
  getDemoSessionRole,
  getDemoTaPaymentModel,
  getDemoUserScenario,
  isReimaginedDemoMode,
  productHasTuitionAssistance,
  type DemoClientId,
  type DemoProductId,
  type DemoSessionRoleId,
  type DemoSessionSelection,
  type DemoTaPaymentModelId,
  type DemoUserScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

/**
 * Relationship types treated as internal TIO staff for the (currently
 * disabled) production gate. The precise definition is intentionally
 * conservative — superuser + TIO approver — and will be finalised when
 * `demoModeRequireStaffRoleInProd` is turned on.
 */
const STAFF_RELATIONSHIP_TYPES = new Set<string>([
  'TAS.Admin.SUPERUSER',
  'TAS.Approver.TIO_APPROVER',
]);

/**
 * The capability keys demo "owns" — the active scenario's product/feature flags
 * (career-pathways `DemoScenario.features`). The demo capability source speaks
 * only for these (and only while a session is active); it abstains on everything
 * else — including `demoMode` itself, which {@link DemoModeService.isAvailable}
 * reads, so that key keeps resolving from the static flag tier (no re-entrancy).
 */
const DEMO_SCENARIO_CAPABILITY_KEYS = new Set<string>([
  'tuitionAssistance',
  'studentLoanRepayment',
  'careerPathways',
  'financialWellness',
  'pslf',
  'employerMatch',
]);

/**
 * The single chokepoint for demo mode.
 *
 * It owns the availability gate (environment + role) and mediates every read /
 * write of demo-session state, so routes, components, the hotkey modal — and,
 * in a later phase, the request-manager demo handler — never sniff
 * localStorage / URL params or the environment directly.
 *
 * Phase 0 delegates the underlying session state to the existing
 * `career-pathways-demo-data` helpers; later phases move that state behind a
 * typed scenario registry and a namespaced storage scheme without changing
 * this public surface.
 */
export default class DemoModeService extends Service {
  @service declare features: FeaturesService;
  @service declare sessionContext: SessionContextService;

  #originalFetch?: typeof fetch;

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    this.#installFetchInterceptor();
  }

  /**
   * Installs a global `fetch` interceptor (seam B) so that raw-fetch paths that
   * bypass the store / request manager — service methods (e.g. limits status)
   * and custom adapter methods (e.g. tas-participant eligibility) — also return
   * demo fixtures while a demo session is active. It is a transparent
   * pass-through whenever demo mode is inactive or no fetch-matcher matches, and
   * is restored when the service is torn down.
   */
  #installFetchInterceptor(): void {
    if (typeof window === 'undefined' || this.#originalFetch) {
      return;
    }
    const original = window.fetch.bind(window);
    this.#originalFetch = original;

    const intercept: typeof fetch = (input, init) => {
      try {
        const demoResponse = this.#demoResponseFor(input, init);
        if (demoResponse) {
          return Promise.resolve(demoResponse);
        }
      } catch {
        // Never let demo interception break a real request.
      }
      return original(input, init);
    };

    window.fetch = intercept;
    registerDestructor(this, () => {
      if (this.#originalFetch && window.fetch === intercept) {
        window.fetch = this.#originalFetch;
      }
    });
  }

  #demoResponseFor(input: RequestInfo | URL, init?: RequestInit): Response | undefined {
    let url: string;
    let method: string | undefined;
    if (typeof input === 'string') {
      url = input;
      method = init?.method;
    } else if (input instanceof URL) {
      url = input.href;
      method = init?.method;
    } else {
      url = input.url;
      method = init?.method ?? input.method;
    }

    const match = this.#demoFetchMatch(url, method ?? 'GET');
    if (!match) {
      return undefined;
    }
    return new Response(JSON.stringify(match.json), {
      status: match.status ?? 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /**
   * Finds the active scenario's fetch-matcher for a raw `url`+`method` while a
   * demo session is active, else `undefined`. The single matching point shared
   * by the fetch interceptor (which also honours the matcher's `status`) and
   * `demoJsonFor` (which exposes just the body to adapter call sites).
   */
  #demoFetchMatch(url: string, method: string): FetchMatcher | undefined {
    if (!this.isActive) {
      return undefined;
    }
    const verb = method.toUpperCase();
    const resolved = resolveDemoScenario(this.currentSelection);
    return resolved?.fetchMatchers?.find((fetchMatcher) => {
      if (fetchMatcher.method && fetchMatcher.method.toUpperCase() !== verb) {
        return false;
      }
      if (fetchMatcher.urlMatches) {
        return fetchMatcher.urlMatches(url);
      }
      return fetchMatcher.urlIncludes ? url.includes(fetchMatcher.urlIncludes) : false;
    });
  }

  /**
   * Returns the demo response body for a raw `url`+`method` while a demo session
   * is active (matched against the active scenario's fetch-matchers), or the
   * `undefined` sentinel when there is no active demo match. The body is `unknown`
   * because it mirrors arbitrary backend JSON; `FetchMatcher.json` is always
   * defined, so `undefined` unambiguously means "no match, fall through to the
   * network". Used by the fetch interceptor AND directly by custom adapter
   * methods whose `ajax` bypasses `window.fetch` (e.g. the tas-participant
   * eligibility endpoints), so those raw paths return fixtures reliably.
   */
  demoJsonFor(url: string, method = 'GET'): unknown {
    const match = this.#demoFetchMatch(url, method);
    return match ? match.json : undefined;
  }

  /**
   * Expose demo state as a {@link CapabilitySource} for the features service
   * (highest rank). While a demo session is active it answers the active
   * scenario's product/feature flags; otherwise — and for any non-scenario key —
   * it abstains, so `features.can(...)` falls through to beta/flag/default.
   */
  asCapabilitySource(): CapabilitySource {
    return {
      id: 'demo',
      rank: CAPABILITY_SOURCE_RANK.demo,
      resolve: (key) => {
        if (!this.isActive || !DEMO_SCENARIO_CAPABILITY_KEYS.has(key as string)) {
          return undefined;
        }
        return isFeatureEnabled(key as Parameters<typeof isFeatureEnabled>[0]);
      },
    };
  }

  /**
   * Whether the demo system is reachable in this session. Fails safe to
   * `false` when the master flag is absent. Enabled in every environment
   * today; flip `featureFlags.demoModeRequireStaffRoleInProd` to restrict
   * production to internal staff.
   */
  get isAvailable(): boolean {
    try {
      if (!this.features.isEnabled('demoMode')) {
        return false;
      }
      if (this.features.tioEnvironment !== 'production') {
        return true;
      }
      if (!this.features.isEnabled('demoModeRequireStaffRoleInProd')) {
        return true;
      }
      return this.isStaffUser;
    } catch {
      // `features.isEnabled` throws if the features service has not been seeded
      // with ENV yet. Because this getter gates the root application template,
      // fail closed (demo unavailable) rather than crash the app.
      return false;
    }
  }

  /** True when a demo session is currently applied (and demo mode is allowed). */
  get isActive(): boolean {
    return this.isAvailable && isReimaginedDemoMode();
  }

  /** Whether the current role is internal TIO staff (used by the prod gate). */
  get isStaffUser(): boolean {
    const relationshipType = this.sessionContext.currentRole?.relationshipType;
    return relationshipType ? STAFF_RELATIONSHIP_TYPES.has(relationshipType) : false;
  }

  // --- Scenario option catalogue (drives the picker UI) ---------------------

  get clients() {
    return DEMO_CLIENT_LIST;
  }

  get products() {
    return DEMO_PRODUCT_LIST;
  }

  get roles() {
    return DEMO_SESSION_ROLE_LIST;
  }

  get userScenarios() {
    return DEMO_USER_SCENARIO_LIST;
  }

  get taPaymentModels() {
    return DEMO_TA_PAYMENT_MODEL_LIST;
  }

  // --- Current selection ----------------------------------------------------

  get currentSelection(): Required<DemoSessionSelection> {
    return {
      clientId: getCurrentDemoClientId(),
      productId: getCurrentDemoProductId(),
      roleId: getCurrentDemoRoleId(),
      userScenarioId: getCurrentDemoUserScenarioId(),
      taPaymentModelId: getCurrentDemoTaPaymentModelId(),
    };
  }

  clientName(clientId: DemoClientId): string {
    return getDemoData(clientId).client.name;
  }

  productName(productId: DemoProductId): string {
    return getDemoProduct(productId).name;
  }

  roleName(roleId: DemoSessionRoleId): string {
    return getDemoSessionRole(roleId).name;
  }

  userScenarioName(userScenarioId: DemoUserScenarioId): string {
    return getDemoUserScenario(userScenarioId).name;
  }

  taPaymentModelName(taPaymentModelId: DemoTaPaymentModelId): string {
    return getDemoTaPaymentModel(taPaymentModelId).name;
  }

  /**
   * Whether the product's scenario enables Tuition Assistance. The TA payment
   * model is a TA-only axis (assistance manner + direct-pay settings), so the
   * picker only offers it for products that pass this check.
   */
  isTaProduct(productId: DemoProductId): boolean {
    return productHasTuitionAssistance(productId);
  }

  /** Apply a demo session (persists the selection and reloads into demo mode). */
  apply(selection: DemoSessionSelection): void {
    applyDemoSession(selection);
  }
}
